<template>
  <PromoBanner v-show="showPageHeader" />
  <header
    v-show="showPageHeader"
    ref="navigation"
    :class="
      $b({
        mainMenuOpen,
        isBackdropActive,
        isScrolled,
        isScrollingDown
      })
    "
  >
    <nav :class="$b('wrapper')">
      <!-- Main nav items -->
      <div :class="$b('main')">
        <!-- Logo -->
        <VisionLink
          :to="resolveLocalePath('/')"
          title="SunGod Homepage"
          display="block"
          :class="$b('logo')"
          @click="handleLogoClick"
        >
          <span class="sr-only">SunGod</span>
          <BaseLogo :class="$b('logo-svg')"/>
        </VisionLink>
        <!-- Main links: Sunglasses, Goggles, etc... -->
        <ul
          v-if="!simplified"
          :class="$b('level-1-links')"
        >
          <li
            v-for="(primaryNavItem, primaryNavItemIndex) in mainNavigationData
              .navigation.mainNavigationItems"
            :key="primaryNavItem.id"
            :style="`--level-1-index: ${primaryNavItemIndex}`"
            :class="$b('level-1-wrapper', {
              hidden: primaryNavItem.showOnMenuBar === 'hide'
            })"
          >
            <component
              :is="primaryNavItem.link?.internalLink ? 'a' : 'button'"
              v-if="primaryNavItem._modelApiKey === 'navigation_button'"
              :class="$b('level-1-link')"
              :href="buildInternalLink(primaryNavItem.link ?? null)"
              @click="
                toggleLevel2Menu({
                  activeIndex: primaryNavItemIndex + 1,
                  title: primaryNavItem.buttonText
                })
              "
            >
              <span :class="$b('level-1-link-text')">
                {{ primaryNavItem.buttonText }}
              </span>
            </component>
            <a
              v-else-if="
                primaryNavItem._modelApiKey === 'main_navigation_link' &&
                  primaryNavItem.internalLink?.slug
              "
              :class="$b('level-1-link', {'has-theme': primaryNavItem.gradient})"
              :href="buildInternalLink(primaryNavItem)"
              @click="handleLevel1Event(primaryNavItem.linkText)"
            >
              <span :class="$b('level-1-link-text')">
                {{ primaryNavItem.linkText }}
              </span>
            </a>
          </li>
        </ul>
      </div>
      <!-- Secondary nav items -->
       <ul :class="$b('secondary-links')">
        <li
          v-for="(secondaryNavItem, secondaryNavItemIndex) in mainNavigationData.navigation.secondaryNavigationItems"
          :key="secondaryNavItemIndex"
          :class="$b('level-1-wrapper', {
            hidden: secondaryNavItem.showOnMenuBar === 'hide'
          })"
        >
          <a
            v-if="secondaryNavItem._modelApiKey === 'navigation_link'"
            :href="buildInternalLink(secondaryNavItem ?? null)"
            :class="$b('level-1-link')"
            @click="handleLevel1Event(secondaryNavItem.linkText)"
          >
            <span :class="$b('level-1-link-text')">
              {{ secondaryNavItem.linkText }}
            </span>
          </a>
          <component
            :is="secondaryNavItem.link?.internalLink ? 'a' : 'button'"
            v-if="secondaryNavItem._modelApiKey === 'navigation_button'"
            :class="$b('level-1-link')"
            :href="buildInternalLink(secondaryNavItem.link ?? null)"
            @click="
              toggleLevel2SecondaryMenu({
                activeIndex: secondaryNavItemIndex + 1,
                title: secondaryNavItem.buttonText
              })
            "
          >
            <span :class="$b('level-1-link-text')">
              {{ secondaryNavItem.buttonText }}
            </span>
          </component>
        </li>
       </ul>
      <!-- Utilities -->
      <ul :class="$b('utilities')">
        <li :class="$b('utility-item')">
          <VisionLink
            :class="$b('utility-button')"
            :is-button="true"
            @click="handleCartClick"
          >
            <span :class="$b('utility-icon')">
              <VisionIcon
                icon="bag"
                colour="black"
                gradient="none"
                size="stretch"
                :class="$b('utility-icon-svg')"
              />
              <span :class="[$b('cart-icon-basket-count'), 'ignore-theme']">
                <ClientOnly>
                  {{ shopifyStore.getOptimisticCartCount }}
                </ClientOnly>
              </span>
            </span>
          </VisionLink>
        </li>
        <li
          v-if="!simplified"
          :class="
            $b('utility-item', {
              menu: true,
            })
          "
          @click="openMainMenu"
        >
          <button :class="$b('utility-button')">
            <span :class="$b('utility-icon-menu')" />
          </button>
        </li>
      </ul>
    </nav>
  </header>
  <NavigationSidebar
    v-if="showPageHeader"
    :main-navigation-items="mainNavigationData.navigation.mainNavigationItems"
    :secondary-navigation-items="mainNavigationData.navigation.secondaryNavigationItems"
    :default-active-submenu="activeSubmenu"
    :default-active-secondary-submenu="activeSecondarySubmenu"
  />
</template>
<script lang="ts">
import navigationQuery, { type Navigation } from '~/gql/navigation.gql'
import { throttleFunction } from '~/assets/js/helpers'

export default defineNuxtComponent({
  name: 'Navigation',

  props: {
    simplified: { type: Boolean, default: false },
  },

  async setup() {
    const { resolveLocalePath, buildInternalLink } = useUrlBuilder()
    const sidebarStore = useSidebarStore()
    const shopifyStore = useShopifyStore()
    const { localeForDato } = useDatoLocale()
    const { isMdBreakpointOrAbove } = useBreakpoint()
    const mainMenuOpen = ref(false)
    const activeLevel2Menu = ref(null)
    const activeLevel3Menu = ref(null)
    const isLoaded = ref(false)
    const isBackdropActive = ref(false)
    const isScrollingDown = ref(false)
    const isScrolled = ref(false)
    const activeSubmenu = ref<{ activeIndex: number, title: string } | null>(null)
    const activeSecondarySubmenu = ref<{ activeIndex: number, title: string } | null>(null)
    let previousScroll = 0
    const navigation = useTemplateRef('navigation')

    const { data: mainNavigationData } = await useDatoQuery<Navigation>({
      query: navigationQuery,
      prefetchedRequest: 'navigation',
      variables: {
        locale: localeForDato,
      },
    })

    const throttledScrollHandler = throttleFunction(() => {

      const announcementBarHeight = parseInt(
        getComputedStyle(navigation.value).getPropertyValue('--promo-bar-height')
      )
      const navHeight = parseInt(
        getComputedStyle(navigation.value).getPropertyValue('--nav-height')
      )
      const scrollTrigger = navHeight + announcementBarHeight

      isScrollingDown.value =
        scrollY > scrollTrigger && scrollY > previousScroll
      isScrolled.value = scrollY > scrollTrigger + 120

      previousScroll = scrollY
    }, 200)

    const pageStore = usePageStore()

    return {
      resolveLocalePath,
      buildInternalLink,
      sidebarStore,
      mainMenuOpen,
      activeLevel2Menu,
      activeLevel3Menu,
      isLoaded,
      isBackdropActive,
      isScrollingDown,
      isScrolled,
      mainNavigationData,
      throttledScrollHandler,
      isMdBreakpointOrAbove,
      shopifyStore,
      activeSubmenu,
      activeSecondarySubmenu,
      pageStore
    }
  },

  methods: {
    handleLogoClick() {
      this.$track.navigation.logoClicked()
    },

    openMainMenu() {
      this.pageStore.toggleNavSidebarOpen(true)
      this.$track.navigation.mobileMenuOpened()
    },

    handleLevel1Event(target) {
      this.$track.navigation.linkClicked({
        tierOne: target,
        location: 'header',
      })
    },

    toggleLevel2Menu(newActiveSubMenu: { activeIndex: number, title: string } | null) {
      this.activeSubmenu = newActiveSubMenu
      this.pageStore.toggleNavSidebarOpen(true)

      this.handleLevel1Event(newActiveSubMenu?.title)
    },

    toggleLevel2SecondaryMenu(newActiveSubMenu: { activeIndex: number, title: string } | null) {
      this.activeSecondarySubmenu = newActiveSubMenu
      this.pageStore.toggleNavSidebarOpen(true)

      this.handleLevel1Event(newActiveSubMenu?.title)
    },

    async handleCartClick() {
      this.shopifyStore.toggleCart(true)
      await this.$track.navigation.cartClicked()
    },
  },

  mounted() {
    this.isLoaded = true
    requestIdleCallback(() => {
      document.addEventListener('scroll', this.throttledScrollHandler)
    })
  },

  beforeUnmount() {
    document.removeEventListener('scroll', this.throttledScrollHandler)
  },

  computed: {
    sidebarOpen() {
      return this.pageStore.navSidebarOpen
    },

    showPageHeader() {
      return !this.pageStore.hidePageHeader
    }
  },

  watch: {
    sidebarOpen(newValue) {
      if (!newValue) {
        this.activeSubmenu = null
        this.activeSecondarySubmenu = null
      }
    }
  }
})
</script>
<style lang="postcss">
.is-dark-chrome-theme {
  .Navigation:not(.Navigation--is-scrolled) {
    .Navigation__wrapper {
      @apply bg-transparent;
    }

    .Navigation__logo-svg {
      @apply fill-white;
    }

    .Navigation__utility-icon-svg {
      --vision-icon-fill: theme('colors.white');
    }

    .Navigation__utility-icon-menu {
      &::before,
      &::after {
        @apply bg-white;
      }
    }

    .Navigation__cart-icon-basket-count {
      @apply text-white;
    }
  }
}

.Navigation {
  @apply sticky top-0 isolate z-[12] w-full shadow-none
  -translate-y-0;
  transition: transform 500ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 250ms linear;

  /**
    Minor State Changes
  */

  /* Backdrop Scrim Active */
  &--is-backdrop-active::before {
    @apply bg-opacity-40 backdrop-blur-[4px];
  }

  &--is-scrolling-down {
    @apply -translate-y-full;
  }

  &--is-scrolled:not(.Navigation--is-scrolling-down) {
    @apply shadow;
  }
}

.Navigation__wrapper {
  @apply relative flex justify-between py-2 h-[var(--nav-height)]
  bg-white bg-opacity-100
  transition-colors duration-300 delay-300;

  padding-left: var(--outer-gutter);
  padding-right: var(--outer-gutter);
}

.Navigation__back-button {
  @apply relative ml-4 mb-4 flex items-center gap-x-2
  text-grey-600 font-semibold text-left
  opacity-0;

  @screen lg {
    @apply hidden;
  }
}

.Navigation__main {
  @apply flex w-full items-center gap-x-12;
}

.Navigation__logo {
  @apply w-20 z-[3] shrink-0;
}

.Navigation__utility-button {
  @apply py-[10px];
}

.Navigation__level-1-links {
  @apply hidden pt-6
    transition-[clip-path] duration-500 delay-150;
  clip-path: inset(0% 0% 0% 100%);

  @screen lg {
    @apply relative flex h-full top-auto
    pt-0 overflow-visible;
    clip-path: none;
  }

  .Navigation__utility-item {
    @apply px-4 opacity-0 w-fit
    transition-all translate-x-5 duration-500 delay-0
    lg:hidden;
  }
}

.Navigation__level-1-wrapper {
  @apply self-center flex mb-3;

  @screen lg {
    @apply h-full mb-0;

    &:first-child {
      .Navigation__level-1-link {
        @apply pl-0;
      }
    }
  }
}

.Navigation__level-1-wrapper--hidden {
  @apply hidden;
}

.Navigation__level-1-link {
  @apply block self-center py-2 px-4
  text-xs font-semibold text-off-black
  opacity-0
  transition-all translate-x-5 duration-500 delay-0;

  @screen lg {
    @apply px-5 py-4 xxl:px-6 opacity-100 translate-x-0 transition-none;
  }
}

.Navigation__level-1-link-text {
  @apply relative block;

  @screen lg {
    &:after {
      @apply content-[''] absolute -bottom-1 left-0 w-full h-0.5
      bg-black
      scale-x-0 origin-right transition-transform duration-500;
    }
  }
}

.Navigation__level-1-button {
  @apply text-off-black text-base font-semibold px-4;
}

/* Desktop Hover State */
@screen lg {
  .Navigation__level-1-wrapper:hover {
    .Navigation__level-1-link-text {
      &:after {
        @apply scale-x-100 origin-left;
      }
    }
  }
}

.Navigation__utilities {
  @apply w-full flex z-[2] justify-end;

  @screen lg {
    @apply w-auto gap-x-2;
  }
}

.Navigation__utility-item {
  @apply flex self-center;

  @screen lgMax {
    &:last-child {
      .Navigation__utility-button {
        @apply pr-0 ml-1;
      }
    }
  }

  @screen lg {
    &:first-child {
      .Navigation__utility-button {
        @apply pr-0;
      }
    }
  }
}

.Navigation__utility-item--menu {
  @screen lg {
    @apply hidden;
  }
}

.Navigation__utility-button {
  @apply h-full px-2 lg:px-4;
}

.Navigation__utility-icon,
.Navigation__utility-icon-menu {
  @apply flex flex-wrap cursor-pointer w-5 md:w-6 relative;
}

.Navigation__utility-icon-menu {
  @apply aspect-square relative flex items-center justify-center flex-col;

  &::before,
  &::after {
    @apply content-[''] absolute w-[calc(100%_+_1px)] h-0.5
    bg-off-black rounded-full
    transition-transform duration-500;
  }

  &::before {
    @apply translate-y-1;
  }

  &::after {
    @apply -translate-y-1;
  }
}

.Navigation__cart-icon-basket-count {
  @apply absolute bottom-[12%] flex items-center justify-center
    text-[9px] md:text-[11px] text-black text-center
    font-semibold
    leading-none;

  left: 50%;
  transform: translateX(-50%);
}


/**

  States

*/

.Navigation__secondary-links {
  @apply hidden lg:flex;
}
</style>
